import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Workout from "components/Workout.jsx";
import api from "api";

const LS_WORKOUT = "tmpWorkout";

const today = () => new Date().toISOString().split("T")[0];

const createWorkoutFromPlan = (plan) => {
  return _.cloneDeep(plan.exercises);
};

const createWorkoutFromLatest = (apiWorkout) => {
  let workout = _.cloneDeep(apiWorkout);
  for (const exercise in workout) {
    workout[exercise].series.forEach((it) => {
      it.done = true;
    });
  }
  return workout;
};

const sortByDone = (workout) => {
  return (e1, e2) => {
    let w1 = workout[e1].series.filter((it) => it.done === true).length;
    let w2 = workout[e2].series.filter((it) => it.done === true).length;
    return w2 - w1;
  };
};

class AddWorkout extends React.Component {
  state = {
    isSaving: false,
    isFetching: true,
    workout: null,
    workoutOrder: null,
    availableExercises: null,
    date: today(),
    progress: {},
    updateTime: null,
  };

  componentDidMount() {
    console.log("componentDidMount - Initial state.workout:", JSON.stringify(this.state.workout, null, 2));

    api.getAvailableExercises().then((availableExercises) => {
      this.setState({ availableExercises: availableExercises });
    });

    // Read workout from local storage
    let localWorkout = localStorage.getItem(LS_WORKOUT);
    console.log("componentDidMount - localWorkout from localStorage:", localWorkout);
    if (localWorkout !== null) {
      localWorkout = JSON.parse(localWorkout);
      console.log("componentDidMount - parsed localWorkout:", JSON.stringify(localWorkout, null, 2));
      if (localWorkout?.date !== today()) {
        localWorkout = null;
        console.log("componentDidMount - localWorkout date mismatch, setting to null");
      } else {
        console.log(
          "componentDidMount - Setting state from localWorkout.done:",
          JSON.stringify(localWorkout.done, null, 2)
        );
        this.setState({
          workout: localWorkout.done,
          workoutOrder: Object.keys(localWorkout.done).sort(sortByDone(localWorkout.done)),
          updateTime: localWorkout.update_time,
        });
      }
    }

    api.getLatestWorkout().then((apiWorkout) => {
      console.log("componentDidMount - apiWorkout from server:", JSON.stringify(apiWorkout, null, 2));
      if (this.state.workout === null && apiWorkout === null) {
        console.log("componentDidMount - No workout, getting new plan");
        api.getNewPlan().then((p) => {
          console.log("componentDidMount - New plan:", JSON.stringify(p, null, 2));
          const workoutFromPlan = createWorkoutFromPlan(p);
          console.log("componentDidMount - Workout from plan:", JSON.stringify(workoutFromPlan, null, 2));
          this.setState({ workout: workoutFromPlan, workoutOrder: Object.keys(p.exercises) });
        });
      } else if (
        apiWorkout !== null &&
        (this.state.workout === null || this.state.updateTime < apiWorkout.update_time)
      ) {
        console.log("componentDidMount - Using apiWorkout, apiWorkout.done:", JSON.stringify(apiWorkout.done, null, 2));
        const workoutFromLatest = createWorkoutFromLatest(apiWorkout.done);
        console.log("componentDidMount - Workout from latest:", JSON.stringify(workoutFromLatest, null, 2));
        this.setState({
          workout: workoutFromLatest,
          workoutOrder: Object.keys(apiWorkout.done),
          progress: apiWorkout.progress,
          updateTime: apiWorkout.update_time,
        });
      }
    });
  }

  componentDidUpdate = () => {
    const isFetching = this.state.workout === null || this.state.availableExercises === null;

    if (isFetching !== this.state.isFetching) {
      this.setState({ isFetching: isFetching });
    }
  };

  goToSummary = () => {
    document.location = "/summary";
  };

  onRepetitionsChange = (exercise, index, repetitions) => {
    var newState = { ...this.state };

    // Always update the serie at index
    newState.workout[exercise].series[index].repetitions = repetitions;

    // Update also future series not completed yet
    newState.workout[exercise].series.forEach((serie, i) => {
      if (i > index && !serie.done) {
        serie.repetitions = repetitions;
      }
    });
    this.store(newState);
  };

  onWeightChange = (exercise, index, weight) => {
    var newState = { ...this.state };

    // Always update the serie at index
    newState.workout[exercise].series[index].weight = weight;

    // Update also future series not completed yet
    newState.workout[exercise].series.forEach((serie, i) => {
      if (i > index && !serie.done) {
        serie.weight = weight;
      }
    });
    this.store(newState);
  };

  onDoneChange = (exercise, index, done) => {
    console.log("onDoneChange - this.state.workout:", JSON.stringify(this.state.workout, null, 2));
    var newState = { ...this.state };
    console.log("onDoneChange - newState.workout after spread:", JSON.stringify(newState.workout, null, 2));
    let series = newState.workout[exercise].series;
    series[index].done = done;
    if (series.every((s) => s.done)) {
      series.push({ ...series[series.length - 1], done: false });
    }
    newState.workoutOrder.sort(sortByDone(newState.workout));
    console.log("onDoneChange - newState.workout before store:", JSON.stringify(newState.workout, null, 2));
    this.store(newState);
  };

  onExerciseAdded = (exercise, series, reps, weight) => {
    var newState = { ...this.state };
    if (!(exercise in newState.workout)) {
      newState.workout[exercise] = {
        series: _.times(series, function () {
          return {
            weight: weight,
            repetitions: reps,
          };
        }),
      };
      newState.workoutOrder.push(exercise);
    }
    // TODO shoudln't be necessary to post api, as it only changes the plan
    this.store(newState);
  };

  onExerciseRemoved = (exercise) => {
    var newState = { ...this.state };
    if (exercise in newState.workout) {
      delete newState.workout[exercise];
      newState.workoutOrder = newState.workoutOrder.filter((it) => it !== exercise);
    }
    this.store(newState);
  };

  store = (newState) => {
    console.log("store - newState.workout:", JSON.stringify(newState.workout, null, 2));
    this.setState(newState);
    this.setState({ isSaving: true });

    let newWorkout = {
      done: _.cloneDeep(newState.workout),
      date: newState.date,
    };
    console.log("store - newWorkout being sent to API:", JSON.stringify(newWorkout, null, 2));

    api.createWorkout(newWorkout).then((r) => {
      console.log("store - API response:", JSON.stringify(r, null, 2));
      console.log("store - newState.workout when creating localWorkout:", JSON.stringify(newState.workout, null, 2));
      let localWorkout = {
        done: newState.workout,
        date: newState.date,
        update_time: r.workout.update_time,
      };
      console.log("store - localWorkout to save:", JSON.stringify(localWorkout, null, 2));
      localStorage.setItem(LS_WORKOUT, JSON.stringify(localWorkout));

      this.setState({
        progress: r.workout.progress,
        updateTime: r.workout.update_time,
        isSaving: false,
      });
    });
  };

  render() {
    const { isFetching, workout, workoutOrder, progress, date, availableExercises } = this.state;
    return isFetching ? (
      <div align="center">
        <CircularProgress color="secondary" />
      </div>
    ) : (
      <div>
        <div>
          <Workout
            date={date}
            workout={workout}
            workoutOrder={workoutOrder}
            progress={progress}
            availableExercises={availableExercises}
            onRepetitionsChange={this.onRepetitionsChange}
            onWeightChange={this.onWeightChange}
            onDoneChange={this.onDoneChange}
            onExerciseAdded={this.onExerciseAdded}
            onExerciseRemoved={this.onExerciseRemoved}
          />
        </div>
      </div>
    );
  }
}

AddWorkout.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles({
  bottomButton: { textAlign: "center", marginTop: "50px" },
})(AddWorkout);
